import React, { Suspense, useContext, useMemo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUserStats from '../hooks/useUserStats';
import useGamification from '../hooks/useGamification';
import useTriviaSession from '../hooks/useTriviaSession';
import useTriviaGame from '../hooks/useTriviaGame';
import useTriviaLocalStats from '../hooks/useTriviaLocalStats';
import useHomeHoroscope from '../hooks/useHomeHoroscope';
import useHomeNotifications from '../hooks/useHomeNotifications';
import useServerClock from '../hooks/useServerClock';
import useHomeStatsEffects from '../hooks/useHomeStatsEffects';
import useDevTriviaSeed from '../hooks/useDevTriviaSeed';
import useApiVersionSync from '../hooks/useApiVersionSync';
import useHomeScrollStability from '../hooks/useHomeScrollStability';
import useRouletteHubState from '../hooks/useRouletteHubState';
import useHomeNavigation from '../hooks/useHomeNavigation';
import { getOracleStatus } from '../services/oracleApi';

import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import HoroscopeCard from '../components/home/HoroscopeCard';
import HomeNotificationCenter from '../components/home/HomeNotificationCenter';
import UserDiscoverySection from '../components/home/UserDiscoverySection';
import HomeStyles from '../components/home/HomeStyles';
import useLeaderboard from '../hooks/useLeaderboard';
import {
  API_ORIGIN,
  HOROSCOPE_XP_ENABLED,
  TIME_PER_QUESTION_MS,
  resolveUserId,
  resolveAvatar,
  countryFlagEmoji,
  countryName,
  getSignEmoji,
} from '../utils/homeHelpers';
// triviaHelpers imports removed (now handled by hooks)
import {
  _getPlayedToday,
  
} from '../utils/dailyTrivia';

import {
  leaderboardButtonBase,
  leaderboardButtonActive,
} from '../styles/homeStyles';

const TrivialModal = React.lazy(() => import('../components/home/TrivialModal'));
const RouletteHubModal = React.lazy(() => import('../components/home/RouletteHubModal'));

const DASHBOARD_BG_COLOR = '#080012';

const DASHBOARD_BG =
  'radial-gradient(circle at 48% -10%, rgba(255,215,0,.10), transparent 24%), radial-gradient(circle at 85% 35%, rgba(218,165,32,.08), transparent 28%), linear-gradient(180deg, #070013 0%, #0b0019 46%, #070013 100%)';

const glassPanel = {
  border: '1px solid rgba(218,165,32,.26)',
  background: 'rgba(6, 0, 18, .28)',
  boxShadow: '0 10px 24px rgba(0,0,0,.20), inset 0 1px 0 rgba(255,255,255,.035)',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

const roundIconButtonStyle = {
  width: 58,
  height: 58,
  borderRadius: '50%',
  border: '1.4px solid rgba(255,215,0,.62)',
  background: 'radial-gradient(circle at 32% 24%, rgba(255,228,156,.24), transparent 34%), linear-gradient(145deg, rgba(43, 0, 82, .88), rgba(8, 0, 24, .82))',
  color: '#FFDCA8',
  boxShadow: '0 12px 24px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.12)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 23,
  cursor: 'pointer',
};

const activePeopleGroups = [
  [
    { name: 'Valentina', signKey: 'leo', avatar: 'V' },
    { name: 'Tomás', signKey: 'aquarius', avatar: 'T' },
    { name: 'Camila', signKey: 'libra', avatar: 'C' },
    { name: 'Sofía', signKey: 'pisces', avatar: 'S' },
    { name: 'Mateo', signKey: 'sagittarius', avatar: 'M' },
    { name: 'Anto', signKey: 'taurus', avatar: 'A' },
  ],
  [
    { name: 'Lucas', signKey: 'aries', avatar: 'L' },
    { name: 'Julieta', signKey: 'virgo', avatar: 'J' },
    { name: 'Bruno', signKey: 'capricorn', avatar: 'B' },
    { name: 'Emma', signKey: 'cancer', avatar: 'E' },
    { name: 'Thiago', signKey: 'scorpio', avatar: 'T' },
    { name: 'Martina', signKey: 'gemini', avatar: 'M' },
  ],
  [
    { name: 'Franco', signKey: 'leo', avatar: 'F' },
    { name: 'Lucía', signKey: 'libra', avatar: 'L' },
    { name: 'Agustín', signKey: 'pisces', avatar: 'A' },
    { name: 'Renata', signKey: 'taurus', avatar: 'R' },
    { name: 'Nicolás', signKey: 'sagittarius', avatar: 'N' },
    { name: 'Bianca', signKey: 'aquarius', avatar: 'B' },
  ]
];

const liveActivityItems = [
  { icon: '🪶', translationKey: 'forum', target: 'forum' },
  { icon: '📰', translationKey: 'feed', target: 'feed' },
  { icon: '👥', translationKey: 'rooms', target: 'rooms' },
  { icon: '🎡', translationKey: 'roulette', target: 'roulette' },
  { icon: '🧠', translationKey: 'trivia', target: 'trivia' },
  { icon: '✨', translationKey: 'signInfo', target: 'signInfo' },
];

const ZODIAC_KEY_BY_ALIAS = {
  aries: 'aries',
  tauro: 'taurus',
  taurus: 'taurus',
  geminis: 'gemini',
  gemini: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  scorpio: 'scorpio',
  sagitario: 'sagittarius',
  sagittarius: 'sagittarius',
  capricornio: 'capricorn',
  capricorn: 'capricorn',
  acuario: 'aquarius',
  aquarius: 'aquarius',
  piscis: 'pisces',
  pisces: 'pisces',
};

function normalizeHomeZodiacKey(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return ZODIAC_KEY_BY_ALIAS[normalized] || '';
}


const HomeScreen = () => {
  const { t } = useTranslation(['home', 'common']);

  const { user, logout, token, isAuthReady } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    handleGoToRooms,
    handleGoToProfile,
    handleGoToRoulette,
    handleGoToCompatibles,
    handleGoToOpuestos,
    handleGoToSignInfo,
    handleGoToContacts,
    handleGoToForum,
    handleGoToSupport,
    handleGoToFeed,
    handleGoToZodiacClash,
    handleGoToZodiacCrush,
    handleGoToRouletteHub,
  } = useHomeNavigation({
    navigate,
    logout,
  });

  const handleGoToSynastryRoom = () => {
    navigate('/synastry');
  };

  const handleGoToNatalChart = () => {
    navigate('/natal-chart');
  };

  const {
    forumUnreadCount,
    forumNotifPreview,
    feedUnreadCount,
    feedNotifPreview,
    chatUnreadCount,
    fetchForumUnread,
    markFeedNotificationsReadAll,
    markAllNotificationsRead,
    fetchChatUnread,
    unifiedUnreadCount,
    notifMenuOpen,
    notifAnchorRect,
    bellWrapRef,
    closeNotifMenu,
    handleForumBellClick,
  } = useHomeNotifications({
    user,
    token,
    isAuthReady,
    location,
  });

  const signEmoji = useMemo(() => getSignEmoji(user?.zodiacSign), [user?.zodiacSign]);
  const avatarUrl = useMemo(() => resolveAvatar(user), [user]);
  const getZodiacSignLabel = (value) => {
    const signKey = normalizeHomeZodiacKey(value);
    return signKey ? t(`zodiacSigns.${signKey}`) : String(value || '');
  };
  const [liveActivityIndex, setLiveActivityIndex] = useState(0);
  const [activePeopleIndex, setActivePeopleIndex] = useState(0);

  // UID estable solo si realmente tenemos un ID real.
  // En dev agregamos "-DEVLOCAL", pero solo si hay base.
  // UID base real (el _id mongo del user, etc.)
  const baseUid = resolveUserId(user);

  // ⚠ IMPORTANTE:
  // - /api/trivia/user-stats exige ObjectId válido => SIN sufijo
  // - /api/trivia/leaderboard en dev acepta sufijo para aislarte de prod
  const resolvedUidForStats = baseUid || '';

  const resolvedUidForLeaderboard = process.env.NODE_ENV === 'production'
    ? (baseUid || '')
    : (baseUid ? `${baseUid}-DEVLOCAL` : '');

  // backwards-compat para no tocar TODO el archivo todavía
  const resolvedUid = resolvedUidForStats;
  const resolvedUidLb = resolvedUidForLeaderboard;

  const {
    horoscope,
    horoOpen,
    setHoroOpen,
  } = useHomeHoroscope({
    user,
    token,
    resolvedUid,
    isAuthReady,
  });

// ===== Trivia modal state =====
const [preloadedTrivia] = useState(false);
const [triviaBooting, setTriviaBooting] = useState(false);
  const {
    showTrivia,
    setShowTrivia,
    loadingTrivia,
    setLoadingTrivia,
    questions,
    setQuestions,
    qIndex,
    setQIndex,
    score,
    setScore,
    answered,
    setAnswered,
    pickedIdx,
    setPickedIdx,
    timeLeft,
    setTimeLeft,
    ticking,
    setTicking,
    questionStartedAt,
    setQuestionStartedAt,
    finished,
    setFinished,
    setPlayedToday,
    setTriviaMode,
    markTriviaAsPlayedMode,
    playedToday,
    triviaMode,
    triviaAdvanceTimeoutRef,
    sessionStartedRef,
    spoiledRef,
    triviaOpenInflightRef,
    current,
    total,
    shareScore,
    resetTriviaBaseState,
    startTriviaPlayState,
  } = useTriviaSession();

  const persistTriviaResultRef = useRef(null);
  const closedTriviaWithoutAnswersRef = useRef(false);
  const [triviaStartedAt, setTriviaStartedAt] = useState(null);
  const {
    lbScope,
    setLbScope,
    lbPeriod,
    setLbPeriod,
    lbItems,
    lbLoading,
    lbMyRank,
    lbMyScore,
    lbMyDisplayName,
    lbMyAvatarPath,
    lbMyMetrics,
    lbMonthKey,
    lbError,
    lbHydrated,
    lbSign,
    setLbSign,
    countryCode,
    fetchLeaderboard,
    resetLeaderboardRuntimeCache,
  } = useLeaderboard({
    user,
    resolvedUidLb,
    showTrivia,
  });



  const {
    clockOffsetMs,
    syncServerTime,
  } = useServerClock({
    setTriviaStartedAt,
  });

  useEffect(() => {
    // Limpia clave global legacy si existiera
    try { localStorage.removeItem('zodiacus_trivia_history_v1'); } catch {}
  }, []);

  // === Historial acumulado desde el backend ===
  const {
    userStats,
    setUserStats,
    fetchUserStats,
  } = useUserStats({
    resolvedUid,
  });

  const {
    history,
    setHistory,
    streak,
    setStreak,
    best,
    setBest,
  } = useTriviaLocalStats({
    resolvedUid,
    setUserStats,
  });

  // === Gamification profile ===
  const {
    setGami,
    fetchGamification,
  } = useGamification({
    resolvedUid,
    userStats,
  });

  const {
    
    persistTriviaResult,
    resetTriviaAndClose,
    openTriviaModal,
    handleBeforeUnload,
    handleVisibilityHidden,
    handlePickAnswer,
    nextQuestion,
  } = useTriviaGame({
    resolvedUid,
    token,
    syncServerTime,
    zodiacSign: user?.zodiacSign,
    countryCode,
    triviaStartedAt,
    clockOffsetMs,
    lbScope,
    lbPeriod,
    userStats,
    showTrivia,
    setShowTrivia,
    questions,
    qIndex,
    total,
    score,
    answered,
    current,
    timeLeft,
    ticking,
    questionStartedAt,
    triviaMode,
    playedToday,
    fetchLeaderboard,
    fetchUserStats,
    fetchGamification,
    setLbScope,
    setQuestions,
    setLoadingTrivia,
    setAnswered,
    setPickedIdx,
    setScore,
    setTicking,
    setTimeLeft,
    setQIndex,
    setQuestionStartedAt,
    setFinished,
    setHistory,
    setStreak,
    setBest,
    setPlayedToday,
    setTriviaStartedAt,
    setTriviaMode,
    setUserStats,
    setGami,
    markTriviaAsPlayedMode,
    resetTriviaBaseState,
    sessionStartedRef,
    startTriviaPlayState,
    triviaOpenInflightRef,
    spoiledRef,
    triviaAdvanceTimeoutRef,
    persistTriviaResultRef,
  });

  // Helper to open the trivia modal in play mode.
  // If the user already played today, show the ranking/results view instead of asking the API for new questions.
  const handleOpenTriviaPlay = () => {
    if (showTrivia && triviaMode === 'play' && !sessionStartedRef.current && !answered && !finished) {
      setShowTrivia(false);
      setLoadingTrivia(false);
      setTriviaBooting(false);
      return;
    }
    const todayKey = (() => {
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Argentina/Buenos_Aires',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(Date.now() + (clockOffsetMs || 0)));
      } catch {
        return new Date().toISOString().slice(0, 10);
      }
    })();

    const historyPlayedToday = Array.isArray(history) && history.some((item) => {
      const rawDate = item?.date || item?.day || item?.playedDate || item?.createdAt || item?.playedAt || item?.timestamp;
      if (!rawDate) return false;
      const value = String(rawDate);
      if (value.slice(0, 10) === todayKey) return true;
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Argentina/Buenos_Aires',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(rawDate)) === todayKey;
      } catch {
        return false;
      }
    });

    const statsPlayedToday = Boolean(
      userStats?.playedToday ||
      userStats?.todayPlayed ||
      userStats?.hasPlayedToday ||
      userStats?.alreadyPlayedToday ||
      userStats?.dailyPlayed ||
      String(userStats?.lastPlayedDate || userStats?.lastTriviaDate || userStats?.lastPlayedAt || '').slice(0, 10) === todayKey
    );

    const alreadyPlayedToday = Boolean(
      closedTriviaWithoutAnswersRef.current ||
      playedToday ||
      _getPlayedToday(resolvedUid) ||
      historyPlayedToday ||
      statsPlayedToday
    );

    if (alreadyPlayedToday) {
      try {
        if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current);
      } catch {}

      closedTriviaWithoutAnswersRef.current = false;
      triviaOpenInflightRef.current = false;
      sessionStartedRef.current = false;
      spoiledRef.current = false;

      // Keep the completed-day summary available; leaderboard is a separate
      // in-modal view so the user always has a clear return path.
      setTriviaBooting(false);
      setTriviaMode('played');
      setPlayedToday(true);
      setFinished(true);
      setLoadingTrivia(false);

      fetchLeaderboard(lbScope);
      setShowTrivia(true);
      return;
    }

    triviaOpenInflightRef.current = false;
    sessionStartedRef.current = false;
    spoiledRef.current = false;

    setPlayedToday(false);
    setFinished(false);
    setTriviaBooting(true);
    setLoadingTrivia(true);
    setTriviaMode('play');
    setShowTrivia(false);

    window.setTimeout(async () => {
      try {
        await openTriviaModal('play');
      } finally {
        setTriviaBooting(false);
      }
    }, 0);
  };

  const handleCloseTriviaModal = () => {
    const hasLoadedTriviaQuestions = Array.isArray(questions) && questions.length > 0;
    const closedAfterLoadingWithoutAnswering = Boolean(
      hasLoadedTriviaQuestions &&
      !answered &&
      qIndex === 0 &&
      score === 0 &&
      !finished
    );

    if (closedAfterLoadingWithoutAnswering) {
      try {
        if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current);
      } catch {}

      closedTriviaWithoutAnswersRef.current = true;
      triviaOpenInflightRef.current = false;
      sessionStartedRef.current = false;
      spoiledRef.current = false;

      setTriviaBooting(false);
      setShowTrivia(false);
      setLoadingTrivia(false);
      setTriviaMode('played');
      setPlayedToday(true);
      setFinished(true);
      setAnswered(false);
      setPickedIdx(null);
      setTicking(false);
      setTimeLeft(10);
      setQuestionStartedAt(null);
      setTriviaStartedAt(null);
      return;
    }

    const userActuallyStartedTrivia = Boolean(
      sessionStartedRef.current ||
      hasLoadedTriviaQuestions ||
      answered ||
      qIndex > 0 ||
      score > 0 ||
      finished
    );

    if (!userActuallyStartedTrivia) {
      try {
        if (triviaAdvanceTimeoutRef.current) clearTimeout(triviaAdvanceTimeoutRef.current);
      } catch {}

      triviaOpenInflightRef.current = false;
      sessionStartedRef.current = false;
      spoiledRef.current = false;

      setTriviaBooting(false);
      setShowTrivia(false);
      setLoadingTrivia(false);
      setTriviaMode('play');
      setPlayedToday(false);
      setFinished(false);
      setAnswered(false);
      setPickedIdx(null);
      setQuestions([]);
      setQIndex(0);
      setScore(0);
      setTicking(false);
      setTimeLeft(10);
      setQuestionStartedAt(null);
      setTriviaStartedAt(null);
      return;
    }

    resetTriviaAndClose();
  };

  // debug solo en dev
  if (process.env.NODE_ENV !== 'production') {
    console.debug(
      '[trivia] uidForStats=',
      resolvedUidForStats,
      'uidForLb=',
      resolvedUidForLeaderboard,
      'user=',
      user
    );
  }

  // Sincroniza el flag "ya jugó hoy" cuando tenemos userId resuelto
  useEffect(() => {
    if (showTrivia && triviaMode === 'play' && !finished) return;
    setPlayedToday(_getPlayedToday(resolvedUid));
  }, [resolvedUid, setPlayedToday, showTrivia, triviaMode, finished]);

  useEffect(() => {
    if (showTrivia && (triviaMode !== 'play' || finished || questions.length > 0)) {
      setTriviaBooting(false);
    }
  }, [showTrivia, triviaMode, finished, questions.length]);

  // Helper for capitalizing sign name
  function Cap(str) {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }


  const welcomeRef = useRef(null);

  const oracleEnabled = process.env.REACT_APP_ORACLE_ENABLED === '1';
  const [oracleAccess, setOracleAccess] = useState(null);
  useEffect(() => {
    let active = true;
    if (!oracleEnabled) {
      setOracleAccess(null);
      return () => { active = false; };
    }
    getOracleStatus()
      .then((status) => { if (active) setOracleAccess(Boolean(status?.enabled && (status?.access || status?.hasAccess))); })
      .catch(() => { if (active) setOracleAccess(false); });
    return () => { active = false; };
  }, [oracleEnabled]);

const quickActions = [
  { id: 'support', labelKey: 'quickAccess.support', icon: '💬', action: handleGoToSupport },
  { id: 'synastry', labelKey: 'quickAccess.synastry', icon: '💞', action: handleGoToSynastryRoom },
  { id: 'natalChart', labelKey: 'quickAccess.natalChart', icon: '🪐', action: handleGoToNatalChart },
  { id: 'contacts', labelKey: 'quickAccess.contacts', icon: '📒', action: handleGoToContacts },
  { id: 'rooms', labelKey: 'quickAccess.rooms', icon: '👥', action: handleGoToRooms },
  { id: 'roulette', labelKey: 'quickAccess.roulette', icon: null, action: handleGoToRouletteHub },
  { id: 'forum', labelKey: 'quickAccess.forum', icon: '🪶', action: handleGoToForum },
  { id: 'zodiacClash', labelKey: 'quickAccess.zodiacClash', icon: '⚔️', action: handleGoToZodiacClash },
  { id: 'zodiacCrush', labelKey: 'quickAccess.zodiacCrush', icon: '🍬', action: handleGoToZodiacCrush },
];

  const visibleQuickActions = quickActions.filter(
    (item, index, arr) => arr.findIndex((entry) => entry.id === item.id) === index
  );


  useHomeScrollStability({
    location,
    resolvedUid,
  });




  useHomeStatsEffects({
    resolvedUid,
    fetchUserStats,
    fetchGamification,
    fetchForumUnread,
    fetchChatUnread,
    resetLeaderboardRuntimeCache,
    triviaMode,
    location,
  });
  useDevTriviaSeed({
    resolvedUid,
    setHistory,
    setStreak,
    setBest,
    setUserStats,
    setGami,
  });

  useApiVersionSync({
    fetchUserStats,
    fetchGamification,
  });



  useEffect(() => {
    persistTriviaResultRef.current = persistTriviaResult;
  }, [persistTriviaResult, persistTriviaResultRef]);


const {
  showRouletteHub,
  closeRouletteHub,
  goZodiacal,
  goCompatibles,
  goOpuestos,
} = useRouletteHubState({
  handleGoToRoulette,
  handleGoToCompatibles,
  handleGoToOpuestos,
});


  useEffect(() => {
    if (finished) {
      fetchLeaderboard(lbScope);
    }
  }, [finished, lbScope, fetchLeaderboard]);

  // (moved nextQuestion above)

// Guardar resultado parcial si el usuario cierra/recarga la pestaña durante la trivia
useEffect(() => {
  const onBeforeUnload = () => {
    handleBeforeUnload();
  };
  window.addEventListener('beforeunload', onBeforeUnload);
  return () => window.removeEventListener('beforeunload', onBeforeUnload);
}, [handleBeforeUnload]);

// Salvaguarda: si la pestaña pasa a background, intentamos enviar el parcial
useEffect(() => {
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      handleVisibilityHidden();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => document.removeEventListener('visibilitychange', onVisibility);
}, [handleVisibilityHidden]);


  useEffect(() => {
    const id = window.setInterval(() => {
      setLiveActivityIndex((prev) => (prev + 1) % liveActivityItems.length);
    }, 10000);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActivePeopleIndex((prev) => (prev + 1) % activePeopleGroups.length);
    }, 50000);

    return () => window.clearInterval(id);
  }, []);

  if (!user) return <p>{t('header.loadingUser')}</p>;

  const liveActivity = liveActivityItems[liveActivityIndex] || liveActivityItems[0];
  const activePeople = activePeopleGroups[activePeopleIndex] || activePeopleGroups[0];

  const handleLiveActivityClick = () => {
    if (liveActivity?.target === 'trivia') {
      handleOpenTriviaPlay();
      return;
    }
    if (liveActivity?.target === 'roulette') {
      handleGoToRouletteHub();
      return;
    }
    if (liveActivity?.target === 'forum') {
      handleGoToForum();
      return;
    }
    if (liveActivity?.target === 'rooms') {
      handleGoToRooms();
      return;
    }
    if (liveActivity?.target === 'signInfo') {
      handleGoToSignInfo();
      return;
    }
    handleGoToFeed();
  };

  return (
  <div
    id="homeRoot"
    data-allow-motion="1"
    style={{
      position: 'fixed',
      inset: 0,
      width: '100%',
      height: '100lvh',
      minHeight: '100lvh',
      maxHeight: '100lvh',
      isolation: 'isolate',
      color: '#fff',
      background: DASHBOARD_BG,
backgroundColor: DASHBOARD_BG_COLOR,
backgroundAttachment: 'fixed',
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorY: 'contain',
      paddingBottom: 0,
      backgroundRepeat: 'no-repeat',
backgroundSize: '100% 100%',
backgroundPosition: 'center center',
      // transform: 'translateZ(0)', // REMOVED
      // WebkitTransform: 'translateZ(0)', // REMOVED
    }}
  >
    <HomeStyles />
    <style>{`
      html, body, #root {
  width: 100%;
  height: 100lvh;
  min-height: 100lvh;
  max-height: 100lvh;
  margin: 0;
  background: ${DASHBOARD_BG} !important;
  background-color: ${DASHBOARD_BG_COLOR} !important;
}

      html,
      body {
        overflow: hidden;
      }

      @supports (-webkit-touch-callout: none) {
        html,
        body,
        #root {
          height: 100lvh;
          min-height: 100lvh;
          max-height: 100lvh;
          background: ${DASHBOARD_BG} !important;
          background-color: ${DASHBOARD_BG_COLOR} !important;
        }

        #homeRoot {
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          height: 100lvh !important;
          min-height: 100lvh !important;
          max-height: 100lvh !important;
          background: ${DASHBOARD_BG} !important;
         background-color: ${DASHBOARD_BG_COLOR} !important;
background-repeat: no-repeat !important;
background-size: 100% 100% !important;
background-position: center center !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          padding-bottom: 0 !important;
        }
      }

      #homeRoot {
        --home-bottom-content-space: 220px;
      }

      @media (display-mode: standalone) {
        #homeRoot {
          --home-bottom-content-space: 144px;
        }

        html,
        body,
        #root {
          height: 100lvh;
          min-height: 100lvh;
          max-height: 100lvh;
          background: ${DASHBOARD_BG} !important;
          background-color: ${DASHBOARD_BG_COLOR} !important;
        }

        #homeRoot {
          bottom: 0 !important;
          height: 100lvh !important;
          min-height: 100lvh !important;
          max-height: 100lvh !important;
        }
      }
    `}</style>

    <div
      aria-hidden="true"
      className="wheel"
      style={{
        backgroundImage: 'url("/logo/Zodiacus.jpeg")',
        opacity: 0.34,
        filter: 'drop-shadow(0 0 22px rgba(218,165,32,.18)) saturate(1.06)',
      }}
    />



    <main
      ref={welcomeRef}
      style={{
        position: 'relative',
        zIndex: 2,
        width: 'min(100%, 470px)',
        margin: '0 auto',
        padding: 'calc(env(safe-area-inset-top, 0px) + 18px) 14px calc(env(safe-area-inset-bottom, 0px) + var(--home-bottom-content-space, 220px))',
        boxSizing: 'border-box',
      }}
    >
     <section
        style={{
          display: 'grid',
          gridTemplateColumns: '50px minmax(0, 1fr) 120px',
          alignItems: 'start',
          gap: 9,
          marginBottom: 18,
        }}
      >
        <button
          type="button"
          onClick={handleGoToProfile}
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: '50%',
            border: '2px solid rgba(255,215,0,.72)',
            overflow: 'hidden',
            padding: 0,
            background: 'linear-gradient(145deg,#4B0082,#140027)',
            boxShadow: '0 0 0 3px rgba(255,255,255,.08), 0 0 26px rgba(218,165,32,.42)',
            cursor: 'pointer',
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={t('header.avatarAlt', { name: user?.fullName || 'Zodiacus' })}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ display: 'grid', placeItems: 'center', height: '100%', fontWeight: 900, fontSize: 22 }}>
              {(user?.fullName || 'Z').charAt(0).toUpperCase()}
            </span>
          )}
        </button>

        <div style={{ minWidth: 0, paddingTop: 1 }}>
          <h1
            style={{
              margin: 0,
              color: '#F3C96B',
              fontSize: 'clamp(1.78rem, 7.55vw, 2.48rem)',
              lineHeight: .80,
              fontFamily: 'Georgia, serif',
              letterSpacing: '.01em',
              textShadow: '0 0 22px rgba(218,165,32,.22)',
              whiteSpace: 'nowrap',
            }}
          >
            Zodiacus
          </h1>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap', paddingRight: 0, maxWidth: '100%' }}>
            <strong style={{ fontSize: '.84rem', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('header.greeting', { name: user?.fullName?.split(' ')?.[0] || 'Zodiacus' })}
            </strong>
            <span
              style={{
                padding: '4px 8px',
                borderRadius: 999,
                background: 'linear-gradient(135deg, rgba(138,43,226,.88), rgba(75,0,130,.92))',
                border: '1px solid rgba(255,215,0,.22)',
                color: '#fff',
                fontSize: '.68rem',
                fontWeight: 800,
                whiteSpace: 'nowrap',
              }}
            >
              {signEmoji} {getZodiacSignLabel(user?.zodiacSign)}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, paddingTop: 3, minWidth: 120 }}>
          <HomeNotificationCenter
            notifMenuOpen={notifMenuOpen}
            unifiedUnreadCount={unifiedUnreadCount}
            forumUnreadCount={forumUnreadCount}
            forumNotifPreview={forumNotifPreview}
            feedUnreadCount={feedUnreadCount}
            feedNotifPreview={feedNotifPreview}
            chatUnreadCount={chatUnreadCount}
            notifAnchorRect={notifAnchorRect}
            bellWrapRef={bellWrapRef}
            handleBellClick={handleForumBellClick}
            closeNotifMenu={closeNotifMenu}
            navigate={navigate}
            handleGoToFeed={handleGoToFeed}
            markFeedNotificationsReadAll={markFeedNotificationsReadAll}
            markAllNotificationsRead={markAllNotificationsRead}
          />
          <button
            type="button"
            onClick={handleGoToSignInfo}
            style={{
              width: 36,
              height: 36,
              minWidth: 36,
              flex: '0 0 36px',
              borderRadius: '50%',
              border: '1px solid rgba(218,165,32,.55)',
              background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.18), transparent 34%), rgba(10,0,26,.64)',
              color: '#FFD700',
              fontSize: 18,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              lineHeight: 1,
              padding: 0,
              boxSizing: 'border-box',
              aspectRatio: '1 / 1',
            }}
          >
            🔎
          </button>
          <button
            type="button"
            onClick={handleGoToProfile}
            aria-label={t('header.profile')}
            style={{
              width: 36,
              height: 36,
              minWidth: 36,
              flex: '0 0 36px',
              borderRadius: '50%',
              border: '1px solid rgba(218,165,32,.55)',
              background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.18), transparent 34%), rgba(10,0,26,.64)',
              color: '#FFD700',
              fontSize: 18,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              lineHeight: 1,
              padding: 0,
              boxSizing: 'border-box',
              aspectRatio: '1 / 1',
            }}
          >
            ♙
          </button>
        </div>
      </section>

      {HOROSCOPE_XP_ENABLED && (
        <div style={{ marginBottom: 12 }}>
          <HoroscopeCard
            horoscope={horoscope}
            user={user}
            horoOpen={horoOpen}
            setHoroOpen={setHoroOpen}
            getZodiacSignLabel={getZodiacSignLabel}
          />
        </div>
      )}

      {oracleEnabled && (
        <button
          type="button"
          onClick={() => navigate('/oracle')}
          aria-label={t('oraclePremium.open')}
          style={{
            ...glassPanel,
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 24,
            padding: '17px 16px 16px',
            marginBottom: 12,
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'left',
            color: '#FFF4D2',
            cursor: 'pointer',
            background: 'radial-gradient(circle at 82% 18%, rgba(157,92,255,.18), transparent 34%), radial-gradient(circle at 12% 0%, rgba(255,215,0,.12), transparent 30%), linear-gradient(135deg, rgba(42,12,75,.88), rgba(8,0,24,.88))',
            boxShadow: '0 12px 28px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.08)',
          }}
        >
          <span aria-hidden="true" style={{ position: 'absolute', inset: 0, opacity: .24, backgroundImage: 'radial-gradient(circle, rgba(255,231,161,.8) 1px, transparent 1.5px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
          <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span>
              <span style={{ display: 'block', color: '#FFD979', fontSize: 18, fontWeight: 950, letterSpacing: '.02em' }}>{t('oraclePremium.title')}</span>
              <span style={{ display: 'block', marginTop: 4, color: 'rgba(255,244,210,.82)', fontSize: 13, fontWeight: 700 }}>{t('oraclePremium.subtitle')}</span>
            </span>
            <span style={{ flexShrink: 0, borderRadius: 999, padding: '6px 9px', minHeight: 28, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', color: oracleAccess ? '#2B1749' : '#FFE6A1', background: oracleAccess ? '#FFD979' : 'rgba(255,255,255,.09)', border: '1px solid rgba(255,217,121,.42)', fontSize: 11, fontWeight: 900 }}>
              {oracleAccess ? t('oraclePremium.available') : t('oraclePremium.locked')}
            </span>
          </span>
          <span style={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 13 }}>
            {['love', 'work', 'energy', 'money'].map((key) => <span key={key} style={{ border: '1px solid rgba(255,217,121,.28)', borderRadius: 999, padding: '5px 9px', color: '#FFEBC1', background: 'rgba(255,255,255,.07)', fontSize: 11, fontWeight: 800 }}>{t(`oraclePremium.chips.${key}`)}</span>)}
          </span>
          <span style={{ position: 'relative', zIndex: 1, display: 'block', marginTop: 11, color: 'rgba(255,244,210,.72)', fontSize: 12, lineHeight: 1.35 }}>{t('oraclePremium.description')}</span>
          <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 13, color: '#FFD979', fontSize: 13, fontWeight: 950 }}>
            <span>{t('oraclePremium.open')}</span>
            {!oracleAccess && <span style={{ color: 'rgba(255,244,210,.65)', fontSize: 11, fontWeight: 800 }}>{t('oraclePremium.price')}</span>}
          </span>
        </button>
      )}

      <section style={{ ...glassPanel, borderRadius: 24, padding: '15px 12px 16px', marginBottom: 12, position: 'relative', overflow: 'hidden' }}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 50% 0%, rgba(218,165,32,.08), transparent 32%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            textAlign: 'center',
            color: '#F3C96B',
            letterSpacing: '.32em',
            fontSize: '.72rem',
            fontWeight: 950,
            marginBottom: 16,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {t('quickAccess.title')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 16, columnGap: 10, position: 'relative', zIndex: 1 }}>
          {visibleQuickActions.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={item.action}
              aria-label={t(item.labelKey)}
              style={{
                border: 0,
                background: 'transparent',
                color: '#fff',
                display: 'grid',
                justifyItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: 0,
                minWidth: 0,
              }}
            >
                  <span
                    style={{
                      ...roundIconButtonStyle,
                      width: 'clamp(54px, 13.2vw, 62px)',
                      height: 'clamp(54px, 13.2vw, 62px)',
                      fontSize: 'clamp(20px, 4.8vw, 25px)',
                      overflow: 'hidden',
                      display: 'grid',
                      placeItems: 'center',
                      alignItems: 'center',
                      justifyItems: 'center',
                      position: 'relative',
                    }}
                  >
                    {item.id === 'roulette' ? (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          display: 'block',
                          backgroundImage: 'url("/logo/Zodiacus.jpeg")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: '50% 50%',
                          backgroundSize: '122% 122%',
                          animation: 'spin360 26s linear infinite',
                          transform: 'translate(-50%, -50%) rotate(0deg)',
                          transformOrigin: '50% 50%',
                          filter: 'drop-shadow(0 0 8px rgba(218,165,32,.45))',
                        }}
                      />
                    ) : (
                      item.icon
                    )}
                  </span>
              <span
                style={{
                  fontSize: item.id === 'roulette' ? '.64rem' : '.74rem',
                  fontWeight: 850,
                  whiteSpace: 'normal',
                  lineHeight: 1.15,
                  overflowWrap: 'anywhere',
                  textShadow: '0 2px 10px rgba(0,0,0,.45)',
                }}
              >
                {t(item.labelKey)}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section
        onClick={handleOpenTriviaPlay}
        role="button"
        tabIndex={0}
        aria-label={playedToday ? t('trivia.card.playedToday') : t('trivia.card.openAria')}
        style={{
          ...glassPanel,
          borderRadius: 24,
          padding: '20px 18px',
          marginBottom: 12,
          minHeight: 178,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: -20,
            top: 18,
            width: 168,
            height: 168,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 34% 24%, rgba(255,255,255,.16), transparent 27%), radial-gradient(circle at 50% 58%, rgba(255,215,0,.24), transparent 48%), linear-gradient(145deg, rgba(12,0,34,.24), rgba(20,0,42,.12))',
            border: '1px solid rgba(218,165,32,.42)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 58,
            color: '#FFD700',
            textShadow: '0 0 18px rgba(255,215,0,.34), 0 3px 12px rgba(0,0,0,.42)',
            boxShadow: '0 18px 36px rgba(0,0,0,.24), inset 0 1px 0 rgba(255,255,255,.10)',
          }}
        >
          🧠
        </div>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 8,
            top: 42,
            width: 118,
            height: 118,
            borderRadius: '50%',
            border: '1px solid rgba(255,215,0,.28)',
            boxShadow: '0 0 18px rgba(218,165,32,.18), inset 0 0 22px rgba(183,44,255,.12)',
            opacity: .95,
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '66%' }}>
          <div style={{ color: '#C54AFF', fontSize: '1.06rem', fontWeight: 950, letterSpacing: '.11em', marginBottom: 11, textShadow: '0 0 18px rgba(197,74,255,.36)' }}>
            {t('trivia.card.title')}
          </div>
          <div style={{ opacity: .92, marginBottom: 13, fontSize: '.88rem' }}>
            {t('trivia.card.metadata', {
              questions: t('trivia.card.questions', { count: 3 }),
              seconds: t('trivia.card.seconds', { count: 10 }),
            })}
          </div>
          <p style={{ margin: '0 0 17px', lineHeight: 1.42, opacity: .90, fontSize: '.91rem' }}>
            {t('trivia.card.description')}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenTriviaPlay();
            }}
            style={{
              borderRadius: 999,
              padding: '11px 18px',
              background: playedToday
                ? 'linear-gradient(135deg, rgba(255,255,255,.62), rgba(255,255,255,.34))'
                : 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 45%, #DAA520 100%)',
              color: playedToday ? '#4B0082' : '#FFD700',
              fontWeight: 950,
              letterSpacing: '.04em',
              fontSize: '.84rem',
              cursor: 'pointer',
              border: '1px solid rgba(255,215,0,.42)',
              boxShadow: '0 12px 28px rgba(0,0,0,.30), 0 0 18px rgba(218,165,32,.22)',
            }}
          >
            {t('trivia.card.open')}
          </button>
        </div>
      </section>

      <section style={{ ...glassPanel, borderRadius: 22, padding: '13px 12px', marginBottom: 12 }}>
        <div style={{ color: '#F3C96B', fontSize: '.72rem', fontWeight: 950, letterSpacing: '.24em', marginBottom: 14 }}>
          {t('activePeople.title')} <span style={{ color: '#27e38a' }}>●</span>
        </div>
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 3 }}>
          <button
            type="button"
            onClick={handleGoToFeed}
            style={{ border: 0, background: 'transparent', color: '#fff', display: 'grid', justifyItems: 'center', gap: 8, cursor: 'pointer', minWidth: 62 }}
          >
            <span style={{ ...roundIconButtonStyle, width: 58, height: 58, fontSize: 23 }}>📰</span>
            <span style={{ fontSize: '.74rem' }}>{t('notifications.feed')}</span>
          </button>
          {activePeople.map((p) => (
            <div key={p.name} style={{ display: 'grid', justifyItems: 'center', gap: 7, minWidth: 62 }}>
              <div
                aria-label={t('activePeople.personOnline', {
                  name: p.name,
                  sign: t(`zodiacSigns.${p.signKey}`),
                })}
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: '50%',
                  border: '2px solid rgba(218,165,32,.78)',
                  background: 'linear-gradient(145deg,#8A2BE2,#140027)',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 950,
                  fontSize: 22,
                  position: 'relative',
                  boxShadow: '0 0 18px rgba(138,43,226,.25)',
                }}
              >
                {p.avatar}
                <span style={{ position: 'absolute', right: 1, bottom: 3, width: 12, height: 12, borderRadius: '50%', background: '#27e38a', border: '2px solid #140027' }} />
              </div>
              <span style={{ fontSize: '.72rem', textAlign: 'center', lineHeight: 1.15 }}>
                {p.name}<br /><small style={{ opacity: .78 }}>{t(`zodiacSigns.${p.signKey}`)}</small>
              </span>
            </div>
          ))}
        </div>
      </section>

      <UserDiscoverySection onViewProfile={(id) => navigate(`/profile/${encodeURIComponent(id)}`)} />

      <section
        onClick={handleLiveActivityClick}
        role="button"
        tabIndex={0}
        style={{
          ...glassPanel,
          borderRadius: 24,
          padding: '16px 16px 15px',
          marginBottom: 0,
          minHeight: 150,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 50% 115%, rgba(218,165,32,.12), transparent 34%)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: -28,
            top: -36,
            width: 150,
            height: 150,
            borderRadius: '50%',
            border: '1px solid rgba(218,165,32,.28)',
            background: 'radial-gradient(circle, rgba(218,165,32,.10), transparent 62%)',
            boxShadow: 'none',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ color: '#F3C96B', fontSize: '.72rem', fontWeight: 950, letterSpacing: '.22em' }}>
              {t('explore.title')}
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 9px',
                borderRadius: 999,
                background: 'rgba(39,227,138,.12)',
                border: '1px solid rgba(39,227,138,.30)',
                color: '#7dffbf',
                fontSize: '.72rem',
                fontWeight: 900,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#27e38a', boxShadow: '0 0 12px rgba(39,227,138,.88)' }} />
              {t('explore.suggested')}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '44px 1fr',
              gap: 12,
              alignItems: 'center',
              minHeight: 52,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
                lineHeight: 0,
                background: 'linear-gradient(145deg, rgba(138,43,226,.95), rgba(12,0,34,.94))',
                border: '1px solid rgba(218,165,32,.55)',
                color: '#F3C96B',
                fontSize: 22,
                boxShadow: '0 12px 24px rgba(0,0,0,.30), 0 0 16px rgba(183,44,255,.22)',
                position: 'relative',
                
              }}
            >
              {liveActivity.target === 'roulette' ? (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                    backgroundColor: 'transparent',
                    transform: 'translate(-50%, -50%)',
                    transformOrigin: 'center center',
                    animation: 'spin360 26s linear infinite',
                    filter: 'drop-shadow(0 0 8px rgba(218,165,32,.40))',
                    willChange: 'transform',
                  }}
                />
              ) : (
                liveActivity.icon
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <strong style={{ display: 'block', color: '#fff', fontSize: '.98rem', lineHeight: 1.2 }}>
                {t(`explore.${liveActivity.translationKey}.title`)}
              </strong>
              <span style={{ display: 'block', marginTop: 4, color: 'rgba(255,255,255,.70)', fontSize: '.76rem', fontWeight: 700 }}>
                {t(`explore.${liveActivity.translationKey}.description`)}
              </span>
            </div>
          </div>

          
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              color: '#F3C96B',
              fontSize: '.76rem',
              fontWeight: 900,
              letterSpacing: '.04em',
              padding: '10px 12px',
              borderRadius: 14,
              background: 'rgba(255,255,255,.07)',
              border: '1px solid rgba(255,255,255,.10)',
            }}
          >
            <span>{t(`explore.${liveActivity.translationKey}.cta`)}</span>
            <span aria-hidden="true">›</span>
          </div>
        </div>
      </section>
    </main>



    <nav
      aria-label={t('bottomNav.aria')}
      data-hide-when-keyboard="true"
      style={{
        position: 'fixed',
        left: 14,
        right: 14,
        top: 'auto',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
        zIndex: 45,
        maxWidth: 470,
        margin: '0 auto',
        height: 70,
        borderRadius: 26,
        border: '1px solid rgba(218,165,32,.24)',
        background: 'linear-gradient(145deg, rgba(6,0,18,.96), rgba(12,0,28,.92))',
        boxShadow: '0 -10px 28px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.045)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        alignItems: 'center',
        padding: '7px 10px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transform: 'none',
        WebkitTransform: 'none',
        willChange: 'auto',
      }}
    >
      {[
        { id: 'home', labelKey: 'bottomNav.home', icon: '⌂', action: () => {} },
        { id: 'feed', labelKey: 'bottomNav.feed', icon: '📰', action: handleGoToFeed },
        { id: 'synastry', labelKey: 'bottomNav.synastry', icon: '💞', action: handleGoToSynastryRoom },
        { id: 'roulette', labelKey: 'bottomNav.roulette', icon: null, action: handleGoToRouletteHub },
        { id: 'logout', labelKey: 'bottomNav.logout', icon: '🚪', action: logout },
      ].map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={item.action}
          aria-label={t(item.labelKey)}
          style={{
            border: 0,
            background: 'transparent',
            color: item.id === 'home' ? '#B72CFF' : '#F3C96B',
            display: 'grid',
            justifyItems: 'center',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            minWidth: 0,
            padding: 0,
            fontWeight: 850,
            lineHeight: 1,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              display: 'grid',
              placeItems: 'center',
              lineHeight: 0,
              filter: item.id === 'home' ? 'drop-shadow(0 0 10px rgba(255,215,0,.55))' : 'none',
              overflow: 'visible',
              position: 'relative',
            }}
          >
            {item.id === 'roulette' ? (
              <span
                aria-hidden="true"
                style={{
  position: 'absolute',
  left: 'calc(50% + 12px)',   // un poco más a la derecha
  top: 'calc(50% + 14px)',   // un poco más abajo
  width: 26,
  height: 26,
  display: 'grid',
  placeItems: 'center',
  lineHeight: 0,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
}}
              >
                <img
                  src="/logo/Zodiacus.jpeg"
                  alt=""
                  draggable={false}
                  style={{
                    width: 26,
                    height: 26,
                    display: 'block',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    animation: 'spin360 26s linear infinite',
                    transformOrigin: 'center center',
                    filter: 'drop-shadow(0 0 5px rgba(218,165,32,.45))',
                  }}
                />
              </span>
            ) : (
              item.icon
            )}
          </span>
          <small
  style={{
    fontSize: item.id === 'roulette' ? '.56rem' : '.68rem',
    whiteSpace: 'normal',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    lineHeight: 1.1,
    transform: item.id === 'roulette'
      ? 'translateY(1px)'
      : 'none',
  }}
>
            {t(item.labelKey)}
          </small>
        </button>
      ))}
    </nav>

    {showRouletteHub ? (
      <Suspense fallback={null}>
        <RouletteHubModal
          showRouletteHub={showRouletteHub}
          closeRouletteHub={closeRouletteHub}
          goZodiacal={goZodiacal}
          goCompatibles={goCompatibles}
          goOpuestos={goOpuestos}
        />
      </Suspense>
    ) : null}


    {(preloadedTrivia || showTrivia) && !triviaBooting ? (
      <Suspense fallback={null}>
        <TrivialModal
          preloadedTrivia={preloadedTrivia}
          showTrivia={showTrivia}
          setShowTrivia={setShowTrivia}
          loadingTrivia={loadingTrivia || triviaBooting}
          triviaMode={triviaMode}
          setTriviaMode={setTriviaMode}
          playedToday={triviaMode === 'play' && !finished ? false : playedToday}
          lbScope={lbScope}
          setLbScope={setLbScope}
          lbPeriod={lbPeriod}
          setLbPeriod={setLbPeriod}
          fetchLeaderboard={fetchLeaderboard}
          countryCode={countryCode}
          countryFlagEmoji={countryFlagEmoji}
          countryName={countryName}
          signEmoji={signEmoji}
          lbSign={lbSign}
          setLbSign={setLbSign}
          getSignEmoji={getSignEmoji}
          Cap={Cap}
          getZodiacSignLabel={getZodiacSignLabel}
          lbHydrated={lbHydrated}
          lbLoading={lbLoading}
          lbItems={lbItems}
          resolvedUid={resolvedUid}
          user={user}
          API_ORIGIN={API_ORIGIN}
          lbMyRank={lbMyRank}
          lbMyScore={lbMyScore}
          lbMyDisplayName={lbMyDisplayName}
          lbMyAvatarPath={lbMyAvatarPath}
          lbMyMetrics={lbMyMetrics}
          lbMonthKey={lbMonthKey}
          lbError={lbError}
          current={current}
          qIndex={qIndex}
          total={total}
          score={score}
          timeLeft={timeLeft}
          TIME_PER_QUESTION_MS={TIME_PER_QUESTION_MS}
          pickedIdx={pickedIdx}
          answered={answered}
          handlePickAnswer={handlePickAnswer}
          nextQuestion={nextQuestion}
          finished={finished}
          shareScore={shareScore}
          resetTriviaAndClose={handleCloseTriviaModal}
          streak={streak}
          best={best}
          history={history}
          leaderboardButtonBase={leaderboardButtonBase}
          leaderboardButtonActive={leaderboardButtonActive}
        />
      </Suspense>
    ) : null}

  </div>
);
};

export default HomeScreen;
